// «الصيانة» — a read-only view of the maintenance system (maint.shallal.me), mirrored by the
// server every 15 minutes. Every figure is computed by the server (GET /maintenance/*); this
// page only lays them out. The super admin also gets «مزامنة الآن», the auto-sync switch and the
// branch-mapping card.
import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, KeyRound, Loader2, RefreshCw, Wrench } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorText } from "@/lib/errorText";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { MAINT } from "@/constants/testIds";
import { MaintKpis } from "@/components/maintenance/MaintKpis";
import { BranchTable, TechniciansCard } from "@/components/maintenance/BranchTable";
import { ItemsList } from "@/components/maintenance/ItemsList";
import { ItemSheet } from "@/components/maintenance/ItemSheet";
import { BranchSheet } from "@/components/maintenance/BranchSheet";
import { MappingCard } from "@/components/maintenance/MappingCard";
import { PRESETS, errorLabel, fmtTemplate, fmtWhen } from "@/components/maintenance/maintText";

const STATUS_POLL_MS = 60000;
const RUNNING_POLL_MS = 4000;
const DEFAULT_QUERY = { type: "wo", status: "open", branch: "all", criticality: "all", overdue: false };

function SyncLine({ status, t, lang }) {
    if (!status) return null;
    const last = status.last_run;
    const ok = status.last_ok;
    const failedSince = last && last.status === "error" && (!ok || last.started_at > ok.started_at);
    return (
        <span data-testid={MAINT.syncLine} translate="no" className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[color:var(--ink-muted)]">
            <span data-testid={MAINT.syncState} data-state={status.running ? "running" : !status.configured ? "off" : failedSince ? "error" : ok ? "ok" : "never"}>
                {status.running ? (
                    <span className="inline-flex items-center gap-1 font-semibold text-[color:var(--brand-brown)]">
                        <Loader2 className="h-3 w-3 animate-spin" /><span>{t("mt_sync_running")}</span>
                    </span>
                ) : ok ? (
                    <span><span>{t("mt_last_sync")}</span>: <span className="font-semibold text-[color:var(--ink)]">{fmtWhen(ok.finished_at, lang)}</span></span>
                ) : (
                    <span>{t("mt_never_synced")}</span>
                )}
            </span>
            {failedSince && !status.running ? (
                <span className="font-semibold text-[color:var(--danger)]"><span>· {t("mt_last_error")}</span>: <span>{errorLabel(t, last.error_code)}</span></span>
            ) : null}
            {status.configured ? (
                <span data-testid={MAINT.syncAuto} data-auto={status.auto_sync === false ? "off" : "on"}>
                    · {status.auto_sync === false ? t("mt_auto_sync_off") : fmtTemplate(t("mt_sync_every"), { m: status.interval_minutes })}
                </span>
            ) : null}
        </span>
    );
}

export default function MaintenancePage() {
    const { t, lang, dir } = useI18n();
    const { isSuperAdmin } = useAuth();
    const [status, setStatus] = useState(null);
    const [ov, setOv] = useState(null);
    const [ovErr, setOvErr] = useState(null);
    const [preset, setPreset] = useState("30");
    const [range, setRange] = useState(PRESETS[30]());
    const [query, setQuery] = useState(DEFAULT_QUERY);
    const [item, setItem] = useState(null);
    const [branchRow, setBranchRow] = useState(null);
    const [starting, setStarting] = useState(false);
    const [pending, setPending] = useState(false);
    const [savingAuto, setSavingAuto] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const listsRef = useRef(null);
    const lastOk = useRef(undefined);
    const ovSeq = useRef(0);

    const loadStatus = useCallback(async () => {
        const r = await api.get("/maintenance/status");
        setStatus(r.data);
        return r.data;
    }, []);

    const loadOverview = useCallback(async () => {
        const mine = ++ovSeq.current;
        try {
            const r = await api.get("/maintenance/overview", { params: { from: range.from, to: range.to } });
            if (mine !== ovSeq.current) return;
            setOv(r.data);
            setOvErr(null);
        } catch (e) {
            if (mine === ovSeq.current) setOvErr(apiErrorText(e));
        }
    }, [range.from, range.to]);

    useEffect(() => { loadOverview(); }, [loadOverview]);

    // A new good sync (here or by the scheduler) refreshes the figures and the lists.
    const onStatus = useCallback((st) => {
        const id = st?.last_ok?.id || null;
        if (lastOk.current !== undefined && id !== lastOk.current) {
            loadOverview();
            setReloadKey((k) => k + 1);
        }
        lastOk.current = id;
        if (!st?.running) setPending(false);
    }, [loadOverview]);

    useEffect(() => {
        let alive = true;
        const tick = () => loadStatus().then((st) => { if (alive) onStatus(st); }).catch(() => {});
        tick();
        const id = setInterval(tick, pending || status?.running ? RUNNING_POLL_MS : STATUS_POLL_MS);
        return () => { alive = false; clearInterval(id); };
    }, [loadStatus, onStatus, pending, status?.running]);

    const runNow = async () => {
        setStarting(true);
        try {
            const r = await api.post("/maintenance/sync/run");
            const run = r.data.run;
            if (!run) {
                setPending(true);
                toast.info(t("mt_run_pending"));
            } else if (run.status === "ok") {
                toast.success(t("mt_run_ok"));
            } else {
                toast.error(errorLabel(t, run.error_code));
            }
            onStatus(await loadStatus());
        } catch (e) {
            toast.error(apiErrorText(e));
        } finally {
            setStarting(false);
        }
    };

    const setAutoSync = async (on) => {
        setSavingAuto(true);
        try {
            await api.patch("/maintenance/settings", { auto_sync: on });
            toast.success(t(on ? "mt_auto_sync_saved_on" : "mt_auto_sync_saved_off"));
            onStatus(await loadStatus());
        } catch (e) {
            toast.error(apiErrorText(e));
        } finally {
            setSavingAuto(false);
        }
    };

    const pickPreset = (k) => {
        setPreset(k);
        setRange(PRESETS[k]());
    };
    const setDate = (k) => (e) => {
        const v = e.target.value;
        if (!v) return;
        setPreset(null);
        setRange((r) => {
            const next = { ...r, [k]: v };
            if (next.from > next.to) { if (k === "from") next.to = v; else next.from = v; }
            return next;
        });
    };
    const showItems = (q) => {
        setQuery({ ...DEFAULT_QUERY, ...q });
        setBranchRow(null);
        setTimeout(() => listsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    };

    const configured = status?.configured;
    const counts = status?.counts || {};
    // without a status answer the overview alone decides (a failed /status never hides the figures)
    const hasData = status
        ? (counts.locations || 0) + (counts.work_requests || 0) + (counts.work_orders || 0) > 0
        : !!ov?.rows?.length;
    const busy = starting || pending || !!status?.running;

    const runAction = isSuperAdmin ? (
        <Button data-testid={MAINT.runBtn} onClick={runNow} disabled={!configured || busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span>{t("mt_run_now")}</span>
        </Button>
    ) : null;
    // the 15-minute pull on / off (app_settings maint.auto_sync); «مزامنة الآن» works either way
    const autoAction = isSuperAdmin && status ? (
        <label className="flex h-9 items-center gap-2 text-xs font-semibold text-[color:var(--ink)]">
            <Switch data-testid={MAINT.autoSync} checked={status.auto_sync !== false} disabled={savingAuto}
                    onCheckedChange={setAutoSync} />
            <span>{t("mt_auto_sync")}</span>
        </label>
    ) : null;
    const why = status?.config_error;
    const offTitle = why === "url" ? "mt_not_configured_url" : why === "key_invalid" ? "mt_not_configured_invalid" : "mt_not_configured";

    return (
        <div data-testid={MAINT.page} className="space-y-4">
            <PageHeader icon={Wrench} title={t("mt_title")} subtitle={t("mt_subtitle")}
                        meta={<SyncLine status={status} t={t} lang={lang} />} primaryAction={runAction} actions={autoAction} />

            {status && !configured ? (
                <div data-testid={MAINT.notConfigured} data-reason={why || ""} className="flex items-start gap-3 rounded-xl border border-[color:var(--warning)]/40 bg-[color:var(--warning-soft)] p-4 text-[color:var(--warning-soft-fg)]">
                    <KeyRound className="mt-0.5 h-5 w-5 shrink-0" />
                    <div className="min-w-0">
                        <p className="font-bold"><span>{t(offTitle)}</span></p>
                        <p className="mt-0.5 break-words text-sm"><span>{t(why === "url" ? "mt_not_configured_url_hint" : "mt_not_configured_hint")}</span></p>
                    </div>
                </div>
            ) : null}
            {status?.truncated ? (
                <p data-testid={MAINT.truncated} className="flex items-start gap-2 rounded-xl border border-[color:var(--warning)]/40 bg-[color:var(--warning-soft)] px-3 py-2 text-xs font-semibold text-[color:var(--warning-soft-fg)]">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span>{t("mt_truncated")}</span>
                </p>
            ) : null}

            <div className="flex flex-col gap-2 rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3 sm:flex-row sm:items-end sm:justify-between">
                <div className="grid grid-cols-2 gap-2 sm:w-80">
                    <label className="min-w-0 space-y-1">
                        <span className="block text-[11px] font-bold text-[color:var(--ink-muted)]">{t("mt_from")}</span>
                        <Input type="date" dir="ltr" data-testid={MAINT.dateFrom} value={range.from} max={range.to}
                               onChange={setDate("from")} className="h-10 text-xs" />
                    </label>
                    <label className="min-w-0 space-y-1">
                        <span className="block text-[11px] font-bold text-[color:var(--ink-muted)]">{t("mt_to")}</span>
                        <Input type="date" dir="ltr" data-testid={MAINT.dateTo} value={range.to} min={range.from}
                               onChange={setDate("to")} className="h-10 text-xs" />
                    </label>
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {["7", "30", "90", "mtd"].map((k) => (
                        <button key={k} type="button" data-testid={MAINT.preset(k)} data-active={preset === k ? "true" : "false"}
                                onClick={() => pickPreset(k)}
                                className={`h-9 rounded-full border px-3 text-xs font-bold transition-[background-color,color] duration-150 ${preset === k
                                    ? "border-[color:var(--brand-orange)] bg-[color:var(--brand-orange)] text-white"
                                    : "border-[color:var(--border-soft)] text-[color:var(--ink-muted)] hover:bg-[color:var(--brand-orange-soft)]"}`}>
                            {t(`mt_preset_${k}`)}
                        </button>
                    ))}
                </div>
            </div>

            {ovErr ? <p role="alert" className="rounded-xl border border-[color:var(--danger)]/40 bg-[color:var(--danger-soft)] px-3 py-2 text-sm text-[color:var(--danger-soft-fg)]">{ovErr}</p> : null}

            {!ov ? (
                <div className="space-y-3"><Skeleton className="h-28 w-full" /><Skeleton className="h-64 w-full" /></div>
            ) : !hasData ? (
                <div data-testid={MAINT.empty} className="flex flex-col items-center gap-2 rounded-xl surface-2 px-4 py-12 text-center">
                    <Wrench className="h-8 w-8 text-[color:var(--brand-orange)]" />
                    <p className="font-bold text-[color:var(--ink)]">{t("mt_empty_title")}</p>
                    <p className="max-w-md text-sm text-[color:var(--ink-muted)]">{t("mt_empty_hint")}</p>
                </div>
            ) : (
                <>
                    <MaintKpis network={ov.network} t={t} lang={lang} onPick={showItems} />
                    <p className="text-[11px] text-[color:var(--ink-faint)]">{t("mt_range_note")}</p>
                    <div className="grid gap-4 xl:grid-cols-3">
                        <section className="min-w-0 space-y-2 xl:col-span-2">
                            <h3 className="text-base font-extrabold text-[color:var(--ink)]">{t("mt_branches_title")}</h3>
                            <BranchTable rows={ov.rows} t={t} lang={lang} onOpen={setBranchRow} />
                        </section>
                        <section className="min-w-0 space-y-2 xl:pt-8">
                            <TechniciansCard rows={ov.technicians} t={t} lang={lang} />
                        </section>
                    </div>
                    <div ref={listsRef} className="scroll-mt-4">
                        <ItemsList query={query} onQuery={setQuery} range={range} rows={ov.rows} t={t} lang={lang} dir={dir}
                                   onOpen={(it) => setItem({ type: it.type, id: it.id })} reloadKey={reloadKey} />
                    </div>
                </>
            )}

            {/* not keyed by the sync: a new sync refreshes its rows and keeps unsaved edits */}
            {isSuperAdmin && status ? <MappingCard reloadKey={reloadKey} t={t} lang={lang} dir={dir} onSaved={loadOverview} /> : null}

            <ItemSheet target={item} onClose={() => setItem(null)} onOpen={(b) => setItem({ type: b.type, id: b.id })} t={t} lang={lang} />
            <BranchSheet row={branchRow} range={range} onClose={() => setBranchRow(null)} t={t} lang={lang}
                         onOpenItem={(w) => { setBranchRow(null); setItem({ type: w.type, id: w.id }); }}
                         onShowItems={(r) => showItems({ type: "wo", status: "all", branch: r.bucket })} />
        </div>
    );
}
